import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { BarChart3, ChevronDown, ChevronUp, TrendingDown, TrendingUp, Minus } from 'lucide-react';

const CATEGORIES = ['fuel', 'service', 'maintenance', 'tyres', 'labour', 'insurance', 'license', 'other'];

const fmtR = (v) => {
  const n = Number(v || 0);
  const parts = n.toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `R ${parts.join('.')}`;
};

const monthRange = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const start = `${ym}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  return { start_date: start, end_date: `${ym}-${String(lastDay).padStart(2, '0')}` };
};

const labelForMonth = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
};

const monthOptions = () => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    opts.push({ value, label: labelForMonth(value) });
  }
  return opts;
};

const sumBy = (rows, predicate) => rows.filter(predicate).reduce((s, c) => s + (c.amount || 0), 0);

/**
 * Compute totals for the last N months (ending at anchorYm) grouped by cost_type.
 * Returns { fuel: [m1, m2, ...], service: [...], ... } arrays of length N (oldest → newest).
 */
const buildMonthSeries = (rows, anchorYm, n) => {
  const [y, m] = anchorYm.split('-').map(Number);
  const buckets = {};
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(y, m - 1 - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    buckets[key] = {};
  }
  for (const c of rows) {
    const ym = (c.date || '').slice(0, 7);
    if (!(ym in buckets)) continue;
    buckets[ym][c.cost_type] = (buckets[ym][c.cost_type] || 0) + (c.amount || 0);
  }
  return buckets;
};

const Sparkline = ({ values, positive = 'up-is-bad' }) => {
  if (!values || values.length === 0) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const W = 60, H = 18, pad = 2;
  const step = (W - pad * 2) / Math.max(values.length - 1, 1);
  const points = values.map((v, i) => {
    const x = pad + i * step;
    const y = H - pad - ((v - min) / range) * (H - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const last = values[values.length - 1];
  const first = values[0];
  const trending = last > first;
  const color = trending
    ? (positive === 'up-is-bad' ? '#e11d48' : '#059669') // red vs green
    : (positive === 'up-is-bad' ? '#059669' : '#e11d48');
  return (
    <svg width={W} height={H} className="inline-block" data-testid="category-sparkline">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pad + (values.length - 1) * step} cy={H - pad - ((last - min) / range) * (H - pad * 2)} r="1.8" fill={color} />
    </svg>
  );
};

const Delta = ({ a, b }) => {
  if (!a && !b) return <span className="text-slate-400 text-xs">—</span>;
  if (!a) return <span className="text-emerald-600 text-xs font-semibold">new</span>;
  const pct = ((b - a) / a) * 100;
  const abs = Math.abs(pct).toFixed(0);
  if (Math.abs(pct) < 1) return (
    <span className="inline-flex items-center gap-1 text-slate-500 text-xs font-medium">
      <Minus className="w-3 h-3" />flat
    </span>
  );
  if (pct > 0) return (
    <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-semibold" title={fmtR(b - a)}>
      <TrendingUp className="w-3 h-3" />+{abs}%
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold" title={fmtR(b - a)}>
      <TrendingDown className="w-3 h-3" />-{abs}%
    </span>
  );
};

export const MonthCompareCard = () => {
  const opts = useMemo(monthOptions, []);
  const [open, setOpen] = useState(false);
  const [monthA, setMonthA] = useState(opts[1]?.value || opts[0].value); // previous month
  const [monthB, setMonthB] = useState(opts[0].value); // current month
  const [vehicleId, setVehicleId] = useState('all');
  const [vehicles, setVehicles] = useState([]);
  const [costsA, setCostsA] = useState([]);
  const [costsB, setCostsB] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load vehicles once the card is opened
  useEffect(() => {
    if (open && vehicles.length === 0) {
      api.getVehicles().then(setVehicles).catch(() => setVehicles([]));
    }
  }, [open, vehicles.length]);

  // Compute 6-month history window ending at monthB (inclusive)
  const historyRange = useMemo(() => {
    const [y, m] = monthB.split('-').map(Number);
    const start = new Date(y, m - 6, 1);
    const startYm = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`;
    const range = monthRange(monthB);
    return { start_date: `${startYm}-01`, end_date: range.end_date };
  }, [monthB]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = (baseParams) => vehicleId === 'all' ? baseParams : { ...baseParams, vehicle_id: vehicleId };
    Promise.all([
      api.getCosts(params(monthRange(monthA))),
      api.getCosts(params(monthRange(monthB))),
      api.getCosts(params(historyRange)),
    ]).then(([a, b, h]) => {
      setCostsA(a);
      setCostsB(b);
      setHistoryRows(h);
    }).catch(() => {
      setCostsA([]);
      setCostsB([]);
      setHistoryRows([]);
    }).finally(() => setLoading(false));
  }, [open, monthA, monthB, vehicleId, historyRange]);

  const totalA = costsA.reduce((s, c) => s + c.amount, 0);
  const totalB = costsB.reduce((s, c) => s + c.amount, 0);
  const monthSeries = useMemo(() => buildMonthSeries(historyRows, monthB, 6), [historyRows, monthB]);
  const seriesKeys = useMemo(() => Object.keys(monthSeries), [monthSeries]);
  const seriesFor = (cat) => seriesKeys.map(k => monthSeries[k][cat] || 0);
  const seriesTotal = () => seriesKeys.map(k => Object.values(monthSeries[k]).reduce((s, v) => s + v, 0));

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <button
          type="button"
          className="w-full flex items-center justify-between gap-4"
          onClick={() => setOpen(v => !v)}
          data-testid="compare-months-toggle"
        >
          <CardTitle className="text-base font-heading font-semibold flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-slate-500" />
            Compare Months
            {!open && <span className="text-xs font-normal text-slate-400 ml-2">(spot cost spikes at a glance)</span>}
          </CardTitle>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
      </CardHeader>
      {open && (
        <CardContent className="pt-0 space-y-4">
          {/* Month selectors */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 uppercase tracking-wide">Baseline</span>
              <Select value={monthA} onValueChange={setMonthA}>
                <SelectTrigger className="w-[180px]" data-testid="compare-month-a"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <span className="text-slate-400">vs</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 uppercase tracking-wide">Compare</span>
              <Select value={monthB} onValueChange={setMonthB}>
                <SelectTrigger className="w-[180px]" data-testid="compare-month-b"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-slate-500 uppercase tracking-wide">Vehicle</span>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger className="w-[200px]" data-testid="compare-vehicle-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vehicles</SelectItem>
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}{v.registration ? ` — ${v.registration}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost" size="sm"
                onClick={() => { const a = monthA; setMonthA(monthB); setMonthB(a); }}
                data-testid="compare-swap-btn"
              >
                Swap
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>
          ) : (
            <>
              {/* Header totals */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">{labelForMonth(monthA)}</p>
                  <p className="font-heading text-2xl font-bold text-obsidian mt-1" data-testid="compare-total-a">{fmtR(totalA)}</p>
                  <p className="text-xs text-slate-500 mt-1">{costsA.length} entries</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase tracking-wide text-slate-500">{labelForMonth(monthB)}</p>
                    <Delta a={totalA} b={totalB} />
                  </div>
                  <p className="font-heading text-2xl font-bold text-obsidian mt-1" data-testid="compare-total-b">{fmtR(totalB)}</p>
                  <p className="text-xs text-slate-500 mt-1">{costsB.length} entries</p>
                </div>
              </div>

              {/* Category breakdown */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-4">Category</th>
                      <th className="py-2 pr-4 text-right">{labelForMonth(monthA)}</th>
                      <th className="py-2 pr-4 text-right">{labelForMonth(monthB)}</th>
                      <th className="py-2 pr-4 text-right">Δ</th>
                      <th className="py-2 pr-4 text-right whitespace-nowrap" title="Last 6 months trend">6-mo trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CATEGORIES.map(cat => {
                      const a = sumBy(costsA, c => c.cost_type === cat);
                      const b = sumBy(costsB, c => c.cost_type === cat);
                      if (!a && !b) return null;
                      return (
                        <tr key={cat} className="border-b border-slate-100 last:border-0" data-testid={`compare-row-${cat}`}>
                          <td className="py-2 pr-4 capitalize font-medium text-slate-700">{cat}</td>
                          <td className="py-2 pr-4 text-right font-data text-slate-600">{fmtR(a)}</td>
                          <td className="py-2 pr-4 text-right font-data text-slate-800">{fmtR(b)}</td>
                          <td className="py-2 pr-4 text-right"><Delta a={a} b={b} /></td>
                          <td className="py-2 pr-4 text-right">
                            <Sparkline values={seriesFor(cat)} />
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="bg-slate-50 font-semibold" data-testid="compare-row-total">
                      <td className="py-2 pr-4">Total</td>
                      <td className="py-2 pr-4 text-right font-data">{fmtR(totalA)}</td>
                      <td className="py-2 pr-4 text-right font-data">{fmtR(totalB)}</td>
                      <td className="py-2 pr-4 text-right"><Delta a={totalA} b={totalB} /></td>
                      <td className="py-2 pr-4 text-right">
                        <Sparkline values={seriesTotal()} />
                      </td>
                    </tr>
                  </tbody>
                </table>
                {seriesKeys.length > 0 && (
                  <p className="text-xs text-slate-400 mt-2">
                    Trend covers {labelForMonth(seriesKeys[0])} → {labelForMonth(seriesKeys[seriesKeys.length - 1])}. Red line = rising spend, green = falling.
                  </p>
                )}
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default MonthCompareCard;
